import { Form, Formik } from "formik";
import { PMButton } from "../../../utils/PMButton";
import { PMField } from "../../../utils/PMField";
import PMSwitch from "../../../utils/PMSwitch";
import { PMNotice } from "../../../utils/notifications";
import { PMDialog } from "../../../utils/PMDialog";
import { dispatchWithToast } from "../../../Utils";
import { updateReportAsync } from "../../../../slices/service/reports";
import { useDispatch } from "react-redux";
import * as Yup from "yup";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { HistoriesModalButton } from "../../histories/modal-button";
import {
  buildReportProcessDefaults,
  hasSeriousAnalysisErrors,
  reportNeedsProcessPrefill,
} from "./report-process-defaults";

export const ApproveReportDialog = ({ report, onRefresh, open, onClose }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [errors, setErrors] = useState([]);

  const initialValues = useMemo(
    () => buildReportProcessDefaults(report),
    [report, open]
  );

  const showAnalysisHint =
    open &&
    reportNeedsProcessPrefill(report) &&
    hasSeriousAnalysisErrors(
      report?.analysis_data || {},
      report?.analysis_data?.improvements || []
    );

  const dialogHeaderActions = [
    {
      component: (
        <HistoriesModalButton module={"report_process"} module_param={report?.id} />
      ),
      action: () => onClose(),
      color: "text-gray-500",
    },
  ];

  return (
    <PMDialog
      title={t("Behandle rapport")}
      open={open}
      onClose={onClose}
      headerActions={dialogHeaderActions}
    >
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={Yup.object({
          process_note: Yup.string().when("not_approved", {
            is: true,
            then: (schema) =>
              schema.required(t("service.report.process.process_note_required")),
          }),
        })}
        onSubmit={(values, helpers) => {
          dispatchWithToast(
            dispatch(updateReportAsync(values)),
            (data) => {
              helpers.setStatus({ success: true });
              helpers.setSubmitting(false);
              if (typeof onRefresh === "function") {
                const reportData = data?.data || data;
                onRefresh(reportData);
              }
              onClose();
            },
            () => {
              helpers.setSubmitting(false);
            }
          );
        }}
      >
        {(formik) => (
          <Form>
            {errors &&
              errors.length > 0 &&
              errors.map((error, index) => (
                <PMNotice
                  severity={"error"}
                  key={index}
                  title={t("Error!")}
                  description={error}
                />
              ))}

            {showAnalysisHint ? (
              <div className="mb-4">
                <PMNotice
                  severity="warning"
                  title={t("service.report.process.analysis_prefill_title")}
                  description={t("service.report.process.analysis_prefill_body")}
                />
              </div>
            ) : null}

            <div className="grid grid-cols-2 sm:grid-cols-2 gap-6">
              <div className="col-span-1 sm:col-span-1">
                <PMSwitch
                  label={t("Godkjent")}
                  name="approved"
                  onChange={(e) => {
                    formik.setFieldValue("not_approved", false);
                    formik.handleChange(e);
                  }}
                  {...formik}
                />
              </div>

              <div className="col-span-1 sm:col-span-1">
                <PMSwitch
                  label={t("Ikke godkjent")}
                  name="not_approved"
                  onChange={(e) => {
                    formik.setFieldValue("approved", false);
                    formik.handleChange(e);
                  }}
                  {...formik}
                />
              </div>

              <div className="col-span-2 sm:col-span-2">
                <PMField
                  label={t("Notat")}
                  name="process_note"
                  multiline={true}
                  rows={4}
                  {...formik}
                />
              </div>

              <div className="col-span-2 sm:col-span-2 flex justify-between">
                <PMButton
                  type="button"
                  onClick={() => onClose()}
                  className="bg-slate-400 hover:bg-slate-200 hover-bg- max-w-1/2"
                >
                  {t("Avbryt")}
                </PMButton>
                <PMButton className="ml-10 max-w-1/2" type="submit">
                  {t("Lagre")}
                </PMButton>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </PMDialog>
  );
};
